import { type CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import { lookProps, lookSurface } from '@/entities/appearance';
import { boardColor, LabelStrip, usePrefetchBoard } from '@/entities/board';
import type { AccessibleBoard } from '@/entities/collab';
import type { Workspace } from '@/entities/workspace';
import { BoardLabelEditor } from '@/features/board-label-editor';
import { BoardCardAppearanceDialog, FavoriteButton } from '@/features/board-manage';
import { formatDateTime } from '@/shared/lib/datetime';
import { originStateFrom } from '@/shared/lib/origin';
import {
  CardActions,
  Icons,
  KindIcon,
  MenuItem,
  MenuList,
  MenuRow,
  MetaIcon,
  MetaItem,
  Popover,
  ResourceMeta,
} from '@/shared/ui';

import styles from './HomeDashboard.module.css';

/** Label chips shown on a card; the rest collapse into "+N" (the strip keeps all colors). */
const VISIBLE_LABELS = 3;

export function BoardCard({ board }: { board: AccessibleBoard }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const prefetch = usePrefetchBoard();
  const [editingLabels, setEditingLabels] = useState(false);
  const [styling, setStyling] = useState(false);
  const tileLook = lookProps(board.appearance?.tile);
  const shown = board.labels.slice(0, VISIBLE_LABELS);
  const hidden = board.labels.length - shown.length;
  const editable = board.can_edit_labels && board.workspace_id !== null;
  return (
    <li
      className={`${styles.card} ${lookSurface ?? ''}`}
      data-testid="home-board"
      {...tileLook}
      style={{ ...tileLook.style, '--board-color': boardColor(board) } as CSSProperties}
    >
      <span className={styles.strip} aria-hidden />
      <LabelStrip labels={board.labels} />
      <Link
        to={`/app/boards/${board.id}`}
        state={originStateFrom(location)}
        className={styles.cardLink}
        onPointerEnter={() => void prefetch(board.id)}
        onFocus={() => void prefetch(board.id)}
      >
        <span className={styles.cardName}>
          <KindIcon icon={<Icons.board />} label={t('workspace.objectTypes.board')} />
          <span className={styles.cardTitle} title={board.name}>
            {board.name}
          </span>
        </span>
        <ResourceMeta>
          {board.workspace_name && board.workspace_type === 'team' ? (
            <MetaItem icon={<Icons.workspace />}>{board.workspace_name}</MetaItem>
          ) : (
            <MetaIcon icon={<Icons.user />} label={t('home.personalBoard')} />
          )}
          {board.owner_name && (
            <MetaItem icon={<Icons.crown />}>
              {t('boards.ownerName', { name: board.owner_name })}
            </MetaItem>
          )}
        </ResourceMeta>
        <span className={styles.cardFoot}>
          <span className={styles.relation}>
            {board.relation === 'owned' ? t('home.owned') : t('home.shared')}
          </span>
          {board.created_by_name && board.relation === 'shared' && (
            <span className={styles.cardMeta}>
              {t('home.createdBy', { name: board.created_by_name })}
            </span>
          )}
        </span>
      </Link>
      {(board.labels.length > 0 || editable) && (
        <div className={styles.labelsRow}>
          <ul className={styles.labelChips} aria-label={t('home.boardLabels')}>
            {shown.map((label) => (
              <li key={label.id} className={styles.labelChip} title={label.name}>
                <span aria-hidden className={styles.labelDot} style={{ background: label.color }} />
                <span className={styles.labelName}>{label.name}</span>
              </li>
            ))}
            {hidden > 0 && (
              <li
                className={styles.labelChip}
                title={board.labels
                  .slice(VISIBLE_LABELS)
                  .map((l) => l.name)
                  .join(', ')}
              >
                +{hidden}
              </li>
            )}
          </ul>
          {editable && (
            <BoardLabelEditor
              board={board}
              open={editingLabels}
              onOpenChange={setEditingLabels}
              trigger={(props, open) => (
                <button
                  type="button"
                  {...props}
                  className={styles.addLabel}
                  aria-label={t('home.editLabels', { name: board.name })}
                  title={t('home.editLabels', { name: board.name })}
                  data-open={open ? 'true' : undefined}
                >
                  <Icons.plus />
                </button>
              )}
            />
          )}
        </div>
      )}
      <CardActions>
        <FavoriteButton board={board} />
        <Popover
          label={t('home.cardActions', { name: board.name })}
          width={220}
          alignEnd
          trigger={(props, open) => (
            <button
              type="button"
              {...props}
              className={styles.menuButton}
              aria-label={t('home.cardActions', { name: board.name })}
              title={t('home.cardActions', { name: board.name })}
              data-open={open ? 'true' : undefined}
            >
              <Icons.more />
            </button>
          )}
        >
          {(close) => (
            <MenuList>
              <MenuRow>
                <MenuItem
                  icon={<Icons.board />}
                  onClick={() => {
                    close();
                    void navigate(`/app/boards/${board.id}`, { state: originStateFrom(location) });
                  }}
                >
                  {t('home.open')}
                </MenuItem>
              </MenuRow>
              {editable && (
                <MenuRow>
                  <MenuItem
                    icon={<Icons.tag />}
                    onClick={() => {
                      close();
                      setEditingLabels(true);
                    }}
                  >
                    {t('home.menuEditLabels')}
                  </MenuItem>
                </MenuRow>
              )}
              {board.can_edit_details && (
                <MenuRow>
                  <MenuItem
                    icon={<Icons.palette />}
                    onClick={() => {
                      close();
                      setStyling(true);
                    }}
                  >
                    {t('home.menuAppearance')}
                  </MenuItem>
                </MenuRow>
              )}
              <MenuRow>
                <span className={styles.menuNote}>
                  {board.last_activity_at
                    ? t('home.activityAt', {
                        when: formatDateTime(board.last_activity_at, i18n.language),
                      })
                    : t('home.noActivity')}
                </span>
              </MenuRow>
            </MenuList>
          )}
        </Popover>
      </CardActions>
      {styling && (
        <BoardCardAppearanceDialog
          board={board}
          onClose={() => {
            setStyling(false);
          }}
        />
      )}
    </li>
  );
}

export function WorkspaceCard({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  return (
    <li className={styles.card} data-testid="home-workspace">
      <Link to={`/app/workspaces/${workspace.id}`} className={styles.cardLink}>
        <span className={styles.cardName}>
          <KindIcon
            icon={workspace.workspace_type === 'personal' ? <Icons.user /> : <Icons.workspace />}
            label={
              workspace.workspace_type === 'personal'
                ? t('home.personalWorkspace')
                : t('home.scopeTeam')
            }
          />
          <span className={styles.cardTitle} title={workspace.name}>
            {workspace.workspace_type === 'personal' ? t('home.personalWorkspace') : workspace.name}
          </span>
        </span>
        <span className={styles.relation}>
          {workspace.workspace_type === 'personal'
            ? t('home.scopePersonal')
            : workspace.is_owner
              ? t('home.owned')
              : t('home.shared')}
        </span>
        <span className={styles.cardMeta}>{t(`roles.${workspace.role}`)}</span>
        {workspace.description && <span className={styles.cardMeta}>{workspace.description}</span>}
      </Link>
    </li>
  );
}
